import { FormEvent, useState } from 'react';
import { format, isToday } from 'date-fns';

import style from './SupportWidget.module.scss';

import { SupportWidgetTestAttributes } from 'chaika-test-attributes';
import type { TranslationValues } from 'chaika-translation-data';

import { translate } from '@chaika/i18n/helper';
import { Button, ButtonTheme } from '@components/Button';
import CrossIcon from '@components/Icon/svg/cross.svg';

import { SupportInbox } from './hooks/useSupportInbox';
import { useTicketList } from './hooks/useTicketList';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketStatusFilter } from './TicketStatusFilter';
import { formatTicketNumber } from './utils/ticketNumber';

const MAX_SEARCH_LENGTH = 100;
const MAX_TITLE_LENGTH = 200;

interface TicketListProps {
    inbox: SupportInbox;
    onClose(): void;
}

interface EmptyState {
    text: TranslationValues;
    action: TranslationValues;
    onAction(): void;
}

export function TicketList({ inbox, onClose }: TicketListProps) {
    const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
    const [title, setTitle] = useState('');
    const {
        tickets,
        isLoading,
        error,
        hasNextPage,
        loadNextPage,
        isFetchingNextPage,
        statuses,
        createTicket,
        isCreatingTicket,
        createTicketError,
    } = useTicketList({ filter: inbox.filter, onTicketCreated: inbox.selectTicket });
    const normalizedTitle = title.trim();
    const emptyState = getEmptyState(inbox, () => setIsCreateFormOpen(true));

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (normalizedTitle) {
            createTicket(normalizedTitle);
        }
    };

    return (
        <>
            <header className={style.header}>
                <h2 className={style.title}>{translate('supportTitle')}</h2>
                <button
                    className={style.headerButton}
                    type={'button'}
                    data-test={SupportWidgetTestAttributes.CloseButton}
                    data-testid={SupportWidgetTestAttributes.CloseButton}
                    aria-label={translate('supportClose')}
                    title={translate('supportClose')}
                    onClick={onClose}
                >
                    <CrossIcon />
                </button>
            </header>
            <div className={style.listActions}>
                {isCreateFormOpen ? (
                    <form
                        className={style.newTicketForm}
                        data-test={SupportWidgetTestAttributes.NewTicketForm}
                        data-testid={SupportWidgetTestAttributes.NewTicketForm}
                        onSubmit={handleSubmit}
                    >
                        <input
                            className={style.textInput}
                            type={'text'}
                            value={title}
                            maxLength={MAX_TITLE_LENGTH}
                            placeholder={translate('supportTicketTitlePlaceholder')}
                            aria-label={translate('supportTicketTitlePlaceholder')}
                            data-test={SupportWidgetTestAttributes.TicketTitleInput}
                            data-testid={SupportWidgetTestAttributes.TicketTitleInput}
                            onChange={event => setTitle(event.target.value)}
                        />
                        <div className={style.formActions}>
                            <Button
                                theme={ButtonTheme.Secondary}
                                type={'button'}
                                dataTest={SupportWidgetTestAttributes.CancelCreateTicketButton}
                                disabled={isCreatingTicket}
                                onClick={() => {
                                    setTitle('');
                                    setIsCreateFormOpen(false);
                                }}
                            >
                                {translate('supportCancel')}
                            </Button>
                            <Button
                                theme={ButtonTheme.Main}
                                type={'submit'}
                                dataTest={SupportWidgetTestAttributes.CreateTicketButton}
                                disabled={!normalizedTitle || isCreatingTicket}
                            >
                                {translate('supportCreateTicket')}
                            </Button>
                        </div>
                    </form>
                ) : (
                    <Button
                        theme={ButtonTheme.Main}
                        type={'button'}
                        dataTest={SupportWidgetTestAttributes.NewTicketButton}
                        additionaClassNames={[style.fullWidthButton]}
                        withAddIcon
                        onClick={() => setIsCreateFormOpen(true)}
                    >
                        {translate('supportNewTicket')}
                    </Button>
                )}
                {createTicketError && <div className={style.error}>{translate('supportSendError')}</div>}
                <div className={style.listToolbar}>
                    <div className={style.searchField}>
                        <input
                            className={style.textInput}
                            type={'text'}
                            value={inbox.searchText}
                            maxLength={MAX_SEARCH_LENGTH}
                            placeholder={translate('supportSearchPlaceholder')}
                            aria-label={translate('supportSearchPlaceholder')}
                            onChange={event => inbox.changeSearchText(event.target.value)}
                        />
                        {inbox.searchText !== '' && (
                            <button
                                className={style.searchClearButton}
                                type={'button'}
                                aria-label={translate('supportSearchClear')}
                                title={translate('supportSearchClear')}
                                onClick={() => inbox.changeSearchText('')}
                            >
                                <CrossIcon />
                            </button>
                        )}
                    </div>
                    <TicketStatusFilter
                        filter={inbox.filter.status}
                        statuses={statuses}
                        onChange={inbox.changeStatusFilter}
                        onReset={() => inbox.changeStatusFilter({ kind: 'open' })}
                    />
                </div>
            </div>
            <div
                className={style.ticketList}
                data-test={SupportWidgetTestAttributes.TicketList}
                data-testid={SupportWidgetTestAttributes.TicketList}
            >
                {isLoading && <div className={style.state}>{translate('supportLoading')}</div>}
                {error && <ErrorMessage />}
                {!isLoading && !error && tickets.length === 0 && (
                    <div className={style.state}>
                        <span>{translate(emptyState.text)}</span>
                        <button
                            className={style.textButton}
                            type={'button'}
                            data-test={SupportWidgetTestAttributes.EmptyStateAction}
                            data-testid={SupportWidgetTestAttributes.EmptyStateAction}
                            onClick={emptyState.onAction}
                        >
                            {translate(emptyState.action)}
                        </button>
                    </div>
                )}
                <ul className={style.ticketItems}>
                    {tickets.map(ticket => (
                        <li key={ticket.id}>
                            <button
                                className={style.ticketItem}
                                type={'button'}
                                data-test={SupportWidgetTestAttributes.Ticket}
                                data-testid={SupportWidgetTestAttributes.Ticket}
                                onClick={() => inbox.selectTicket(ticket.id)}
                            >
                                <span className={style.ticketItemRow}>
                                    {ticket.number !== null && (
                                        <span className={style.ticketNumber}>{formatTicketNumber(ticket.number)}</span>
                                    )}
                                    <span className={style.ticketItemTitle}>{ticket.title}</span>
                                    {ticket.lastMessageAt && (
                                        <span className={style.ticketItemTime}>
                                            {formatLastActivity(ticket.lastMessageAt)}
                                        </span>
                                    )}
                                </span>
                                <span className={style.ticketItemRow}>
                                    <TicketStatusBadge ticket={ticket} />
                                    {ticket.unread > 0 && (
                                        <span className={style.unreadCount}>{formatUnreadCount(ticket.unread)}</span>
                                    )}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
                {hasNextPage && (
                    <button
                        className={style.loadMoreButton}
                        type={'button'}
                        data-test={SupportWidgetTestAttributes.LoadMoreTicketsButton}
                        data-testid={SupportWidgetTestAttributes.LoadMoreTicketsButton}
                        disabled={isFetchingNextPage}
                        onClick={() => void loadNextPage()}
                    >
                        {translate('supportLoadMore')}
                    </button>
                )}
            </div>
        </>
    );
}

function getEmptyState(inbox: SupportInbox, openCreateForm: () => void): EmptyState {
    const { filter } = inbox;

    if (filter.q.trim() !== '') {
        return { text: 'supportNothingFound', action: 'supportFilterReset', onAction: inbox.resetFilter };
    }

    switch (filter.status.kind) {
        case 'all':
            return { text: 'supportEmptyTickets', action: 'supportNewTicket', onAction: openCreateForm };
        case 'open':
            return {
                text: 'supportEmptyOpenTickets',
                action: 'supportShowAllTickets',
                onAction: () => inbox.changeStatusFilter({ kind: 'all' }),
            };
        case 'names':
            return { text: 'supportEmptyFilteredTickets', action: 'supportFilterReset', onAction: inbox.resetFilter };
    }
}

function formatLastActivity(lastMessageAt: string): string {
    const lastMessageDate = new Date(lastMessageAt);

    return format(lastMessageDate, isToday(lastMessageDate) ? 'HH:mm' : 'dd.MM.yyyy');
}

function formatUnreadCount(unread: number): string {
    return unread > 99 ? '99+' : String(unread);
}

function ErrorMessage() {
    return (
        <div
            className={style.error}
            data-test={SupportWidgetTestAttributes.Error}
            data-testid={SupportWidgetTestAttributes.Error}
        >
            {translate('supportError')}
        </div>
    );
}
